import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { API_ENDPOINTS } from "../../apiConfig";
import { PlayerDoubleMatch, PlayerMatch } from "../../interfaces";
import { MatchById } from "../../interfaces/Match";

interface PlayerMatchesApiResponse {
  singles: PlayerMatch[];
  doubles: PlayerDoubleMatch[];
}

interface Mistake {
  id: number;
  error: string;
}

interface MatchDetailsContextType {
  matchId: number | null;
  setMatchId: (id: number) => void;
  playerMatches: PlayerMatch[];
  playerDoubleMatches: PlayerDoubleMatch[];
  loading: boolean;
  error: string | null;
  setPlayerMatches: React.Dispatch<React.SetStateAction<PlayerMatch[]>>;
  setPlayerDoubleMatches: React.Dispatch<
    React.SetStateAction<PlayerDoubleMatch[]>
  >;
  matchData: MatchById | null;
  setMatchData: React.Dispatch<React.SetStateAction<MatchById | null>>;
  fetchMatchData: () => Promise<void>;
  fetchMatchDetails: () => Promise<void>; // <-- add this
  homeInputs: { [matchId: number]: string[] };
  setHomeInputs: React.Dispatch<
    React.SetStateAction<{ [matchId: number]: string[] }>
  >;
  awayInputs: { [matchId: number]: string[] };
  setAwayInputs: React.Dispatch<
    React.SetStateAction<{ [matchId: number]: string[] }>
  >;
  clearAllInputs: () => void; // <-- add clearAllInputs
  mistakes: Mistake[];
  setMistakes: React.Dispatch<React.SetStateAction<Mistake[]>>;
}

const MatchDetailsContext = createContext<MatchDetailsContextType | undefined>(
  undefined
);

export const MatchDetailsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [matchId, setMatchId] = useState<number | null>(null);
  const [playerMatches, setPlayerMatches] = useState<PlayerMatch[]>([]);
  const [playerDoubleMatches, setPlayerDoubleMatches] = useState<
    PlayerDoubleMatch[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matchData, setMatchData] = useState<MatchById | null>(null);
  const [mistakes, setMistakes] = useState<Mistake[]>([]);

  // Add input state for home/away legs
  const [homeInputs, setHomeInputs] = useState<{ [matchId: number]: string[] }>(
    {}
  );
  const [awayInputs, setAwayInputs] = useState<{ [matchId: number]: string[] }>(
    {}
  );

  // Move fetchDetails out so it can be exposed
  const fetchMatchDetails = useCallback(async () => {
    if (matchId == null) return;
    setLoading(true);
    setError(null);
    try {
      const url = API_ENDPOINTS.playerMatchesByMatchId(matchId);
      const res = await fetch(url);
      if (!res.ok) throw new Error("Fehler beim Laden der Match-Details");
      const data: PlayerMatchesApiResponse = await res.json();
      // Just set the fetched data as-is (do not map player objects here)
      setPlayerMatches(data.singles || []);
      setPlayerDoubleMatches(data.doubles || []);
      // Populate homeInputs and awayInputs from fetched data
      const newHomeInputs: { [matchId: number]: string[] } = {};
      const newAwayInputs: { [matchId: number]: string[] } = {};
      data.singles.forEach((single: PlayerMatch) => {
        newHomeInputs[single.id] = single.legs
          .filter((leg) => leg.winner === "home")
          .map((leg) => leg.darts?.toString() || "");
        newAwayInputs[single.id] = single.legs
          .filter((leg) => leg.winner === "away")
          .map((leg) => leg.darts?.toString() || "");
      });
      data.doubles.forEach((double: PlayerDoubleMatch) => {
        newHomeInputs[double.id] = double.legs
          .filter((leg) => leg.winner === "home")
          .map((leg) => leg.darts?.toString() || "");
        newAwayInputs[double.id] = double.legs
          .filter((leg) => leg.winner === "away")
          .map((leg) => leg.darts?.toString() || "");
      });

      setHomeInputs(newHomeInputs);
      setAwayInputs(newAwayInputs);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unbekannter Fehler");
    }
    setLoading(false);
  }, [matchId]);

  useEffect(() => {
    if (matchId == null) return;
    fetchMatchDetails();
  }, [matchId, fetchMatchDetails]);

  // Expose fetchMatchData for external use
  const fetchMatchData = useCallback(async () => {
    if (matchId == null) return;
    try {
      const url = API_ENDPOINTS.matchById(matchId);
      const res = await fetch(url);
      if (!res.ok) throw new Error("Fehler beim Laden der Matchdaten");
      const data: MatchById = await res.json();
      setMatchData(data);
    } catch (e) {
      setMatchData(null);
    }
  }, [matchId]);

  useEffect(() => {
    if (matchId == null) return;
    fetchMatchData();
  }, [matchId, fetchMatchData]);

  // Utility to clear all home/away/player inputs
  const clearAllInputs = () => {
    setHomeInputs({});
    setAwayInputs({});
    setPlayerMatches((prev) =>
      prev.map((pm) => ({ ...pm, home_player: null, away_player: null }))
    );
    setPlayerDoubleMatches((prev) =>
      prev.map((dm) => ({
        ...dm,
        home_player_1: null,
        home_player_2: null,
        away_player_1: null,
        away_player_2: null,
      }))
    );
  };

  const value: MatchDetailsContextType = {
    matchId,
    setMatchId,
    playerMatches,
    playerDoubleMatches,
    loading,
    error,
    setPlayerMatches: setPlayerMatches as React.Dispatch<
      React.SetStateAction<PlayerMatch[]>
    >,
    setPlayerDoubleMatches: setPlayerDoubleMatches as React.Dispatch<
      React.SetStateAction<PlayerDoubleMatch[]>
    >,
    matchData,
    setMatchData,
    fetchMatchData,
    fetchMatchDetails: fetchMatchDetails, // <-- add to context value
    homeInputs,
    setHomeInputs,
    awayInputs,
    setAwayInputs,
    clearAllInputs, // <-- add clearAllInputs to context value
    mistakes,
    setMistakes,
  };

  return (
    <MatchDetailsContext.Provider value={value}>
      {children}
    </MatchDetailsContext.Provider>
  );
};

export function useMatchDetailsContext() {
  const ctx = useContext(MatchDetailsContext);
  if (!ctx)
    throw new Error(
      "useMatchDetailsContext must be used within a MatchDetailsProvider"
    );
  return ctx;
}

export { MatchDetailsContext };
